import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@gapartners/ui';
import type { WarmTransferActiveContext } from '@/types/warmTransfer';

type Props = {
  open: boolean;
  context: WarmTransferActiveContext | null;
  loading?: boolean;
  inFlight?: boolean;
  alreadyAccepted?: boolean;
  onAccept: () => void;
  onDecline: () => void;
};

export function WarmTransferIncomingModal({
  open,
  context,
  loading,
  inFlight,
  alreadyAccepted = false,
  onAccept,
  onDecline,
}: Props) {
  const { t } = useTranslation();
  const ctx = context?.context;

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t(
          'components.contactCenter.warmTransferIncomingTitle',
          'Incoming warm transfer consult'
        )}
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Typography variant="body2" color="text.secondary">
            {t('common.loading', 'Loading…')}
          </Typography>
        )}
        {context && !loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="body2" color="warning.main" fontWeight={600}>
              {alreadyAccepted
                ? t(
                    'components.contactCenter.warmTransferAcceptedIncomingHint',
                    'This is the warm consult you just accepted. Accept the call to start the private consult.'
                  )
                : t(
                    'components.contactCenter.warmTransferNotCustomerCall',
                    'This is not a customer call — private consult while customer is on hold.'
                  )}
            </Typography>
            <Typography variant="body2">
              {t('components.contactCenter.warmTransferFromAgent', {
                name:
                  context.initiatedByDisplayName ??
                  context.initiatedByConnectId,
                defaultValue: 'From agent: {{name}}',
              })}
            </Typography>
            {context.targetDisplayName && (
              <Typography variant="body2" color="text.secondary">
                {t('components.contactCenter.warmTransferTargetLine', {
                  name: context.targetDisplayName,
                  type: context.targetType,
                  defaultValue: 'Target: {{name}} ({{type}})',
                })}
              </Typography>
            )}
            {ctx?.customerName && (
              <Typography variant="body2">
                {t('components.contactCenter.warmTransferCustomerOnHoldLine', {
                  name: ctx.customerName,
                  phone: ctx.customerPhone ?? '',
                  defaultValue: 'Customer on hold: {{name}} {{phone}}',
                })}
              </Typography>
            )}
            {ctx?.caseTitle && (
              <Typography variant="body2" color="text.secondary">
                {t('components.contactCenter.warmTransferCaseLine', {
                  id: ctx.caseId ?? '',
                  title: ctx.caseTitle,
                  defaultValue: 'Case: {{id}} · {{title}}',
                })}
              </Typography>
            )}
            {ctx?.segmentNotes && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ whiteSpace: 'pre-wrap' }}
              >
                {ctx.segmentNotes}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="outlined"
          color="inherit"
          onClick={onDecline}
          disabled={inFlight}
        >
          {t(
            'components.contactCenter.warmTransferDeclineConsult',
            'Decline consult'
          )}
        </Button>
        <Button
          variant="contained"
          onClick={onAccept}
          disabled={inFlight || loading}
        >
          {t(
            alreadyAccepted
              ? 'components.contactCenter.warmTransferAcceptConsultCall'
              : 'components.contactCenter.warmTransferAcceptConsult',
            alreadyAccepted ? 'Accept consult call' : 'Accept consult'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
